import {toast} from "react-hot-toast";

import client from "@/lib/api/apolloClient.ts";
import {
  DeleteComponentResponse,
  DeleteIncidentResponse,
  DeleteMetricResponse,
  DeleteStatusPageResult,
  DuplicateStatusPageResult,
  GQL_BULK_DELETE_COMPONENTS,
  GQL_BULK_DELETE_METRICS,
  GQL_DELETE_COMPONENT,
  GQL_DELETE_INCIDENT,
  GQL_DELETE_METRIC,
  GQL_DELETE_STATUS_PAGE,
  GQL_DUPLICATE_STATUS_PAGE,
  GQL_INCIDENTS_COUNTS,
  GQL_STATUS_PAGE_QUERY,
  GQL_STATUS_PAGES_QUERY,
  GQL_STATUS_PAGES_QUERY_WITH_INCIDENT_COUNTS,
  GQL_STATUSPAGE_COMPONENTS,
  GQL_STATUSPAGE_INCIDENTS,
  GQL_STATUSPAGE_SYSTEM_METRICS,
} from "@/lib/queries/statuspages.ts";
import {GQL_EXTERNAL_USERS, GQL_EXTERNAL_USERS_DELETE} from "@/lib/queries/statuspagesExternalUsers.ts";

export const deleteStatusPage = async (id: number) => {
  const {data} = await client.mutate<DeleteStatusPageResult>({
    mutation: GQL_DELETE_STATUS_PAGE,
    variables: {id},
    refetchQueries: [GQL_STATUS_PAGES_QUERY, GQL_STATUS_PAGES_QUERY_WITH_INCIDENT_COUNTS],
  });

  if (data?.deleteStatusPage?.success) {
    toast.success("Status page has been removed");
  }
};

export const duplicateStatusPage = async (id: number): Promise<number> => {
  const {data} = await client.mutate<DuplicateStatusPageResult>({
    mutation: GQL_DUPLICATE_STATUS_PAGE,
    variables: {id},
    refetchQueries: [GQL_STATUS_PAGES_QUERY, GQL_STATUS_PAGES_QUERY_WITH_INCIDENT_COUNTS],
  });

  if (!data?.duplicateStatusPage?.id) {
    const error = "Unable to duplicate status page. ";
    if (data?.duplicateStatusPage?.success === false && data?.duplicateStatusPage?.errors?.__all__) {
      return Promise.reject(error + data?.duplicateStatusPage?.errors?.__all__.join("."));
    } else {
      return Promise.reject(error);
    }
  }
  return Promise.resolve(data.duplicateStatusPage.id);
};

export const deleteStatusPageComponent = async (statusPageID: number, componentID: number) => {
  await client.mutate<DeleteComponentResponse>({
    mutation: GQL_DELETE_COMPONENT,
    variables: {statusPageID, componentID},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_COMPONENTS],
  });
};

export const deleteStatusPageComponents = async (statusPageID: number, componentIDs: number[]) => {
  await client.mutate<DeleteComponentResponse>({
    mutation: GQL_BULK_DELETE_COMPONENTS,
    variables: {statusPageID, componentIDs},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_COMPONENTS],
  });
};

export const deleteSystemMetric = async (statusPageID: number, metricID: number) => {
  await client.mutate<DeleteMetricResponse>({
    mutation: GQL_DELETE_METRIC,
    variables: {statusPageID, metricID},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_SYSTEM_METRICS],
  });
};

export const bulkDeleteSystemMetrics = async (statusPageID: number, metricIDs: number[]) => {
  await client.mutate<DeleteMetricResponse>({
    mutation: GQL_BULK_DELETE_METRICS,
    variables: {statusPageID, metricIDs},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_SYSTEM_METRICS],
  });
};

export const deleteIncident = async (statusPageID: number, incidentID: number) => {
  await client.mutate<DeleteIncidentResponse>({
    mutation: GQL_DELETE_INCIDENT,
    variables: {statusPageID, incidentID},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_INCIDENTS, GQL_INCIDENTS_COUNTS],
  });
};

export const deleteExternalUser = async (statusPageID: number, userId: number) => {
  await client.mutate({
    mutation: GQL_EXTERNAL_USERS_DELETE,
    variables: {statusPageID, userIds: [userId]},
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_EXTERNAL_USERS],
  });
};
